import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { useActiveOrg } from '@/stores/activeOrgStore';
import { getConfig } from '@/lib/config';

let tokenProvider: () => Promise<string | null> = async () => null;

/**
 * Wire the Auth0 token getter into the HTTP client.
 * Called once from main.tsx after the Auth0Provider mounts.
 */
export function setTokenProvider(provider: () => Promise<string | null>): void {
  tokenProvider = provider;
}

/**
 * Get an access token, waiting briefly if one isn't available yet.
 *
 * On a fresh page load a query can fire before TokenBridge has wired the real
 * provider (it's still the `async () => null` default), or during the short
 * window after login where `isAuthenticated` lags. Sending the request without
 * a token in that window is a guaranteed 401. Rather than fire a header-less
 * request and rely on a retry, wait a bounded time for the token to appear.
 * When the user really is logged out the token stays null and we fall through
 * (the request proceeds unauthenticated, as before) after ~1s at most.
 */
async function resolveToken(): Promise<string | null> {
  let token = await tokenProvider();
  for (let attempt = 0; !token && attempt < 10; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 100));
    token = await tokenProvider();
  }
  return token;
}

function createClient(): AxiosInstance {
  // baseURL is resolved per-request from runtime /config.json (loaded by
  // main.tsx before any request is made). axios.create runs at module
  // import time — before loadConfig() resolves — so we can't read
  // getConfig() at construction. Instead the request interceptor sets
  // baseURL on each call.
  const client = axios.create({
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    timeout: 30_000,
  });

  client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    if (!config.baseURL) {
      config.baseURL = getConfig().apiBaseUrl;
    }
    const token = await resolveToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    const activeOrgId = useActiveOrg.getState().activeOrgId;
    if (activeOrgId && !config.headers.has('X-Active-Org-Id')) {
      config.headers.set('X-Active-Org-Id', activeOrgId);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as
        | (InternalAxiosRequestConfig & { _authRetried?: boolean })
        | undefined;
      // A 401 can happen because the request went out before a token was available
      // (the provider briefly returned null during a silent-auth hiccup or the
      // post-login window). Force a token and replay the request once before giving up,
      // so a transient token gap self-heals instead of surfacing as a failed query.
      if (error.response?.status === 401 && config && !config._authRetried) {
        config._authRetried = true;
        const token = await resolveToken();
        if (token) {
          config.headers.set('Authorization', `Bearer ${token}`);
          return client.request(config);
        }
      }
      if (error.response?.status === 401 && typeof window !== 'undefined') {
        // Defer to auth context to handle re-auth; we just signal.
        window.dispatchEvent(new CustomEvent('auth:unauthorized'));
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export const apiClient = createClient();
